import { CreateThreadState } from 'src/redux/slices/threadCategories';
import * as yup from 'yup';
import serializeToPlaintext from 'src/utils/serializeToPlaintext';
import {
  createEditor, Descendant, Editor, Element, Text, Transforms,
} from 'slate';
import { ElementTypes, LinkElement } from 'src/types/CustomSlateTypes';
import { GroupsState } from 'src/redux/slices/groups';
import { getElementsMetadata } from 'src/utils/index';
import hasOnlyAmazonLinks from './hasOnlyAmazonLinks';
import isValidAmazonLink from './isValidAmazonLink';
import serializeToHtml from './serializeToHtml';

export const NONE_OPTION = 'NONE';

const defineEmptyString = (n: Descendant) => {
  if ((n as Element).type) {
    const node = n as Element;
    node.children = (n as Element).children.map(defineEmptyString);
    return node;
  }
  if (typeof (n as Text).text === 'undefined') {
    const node = n as Text;
    node.text = '';
    return node;
  }
  return n;
};

export default {
  title: yup
    .string()
    .ensure()
    .trim()
    .required('title_error_required')
    .min(8, 'title_error_min')
    .max(250, 'title_error_max'),
  content: (isInternalUser: boolean = false) => yup
    .string()
    .transform((value) => serializeToPlaintext(value))
    .ensure()
    .trim()
    .required('content_error_required')
    .min(20, 'content_error_min')
    // @ts-ignore
    .test('htmlMaxLength', 'content_error_max', (value, { originalValue }) => {
      const content = originalValue.map(defineEmptyString);
      const metadataContent = getElementsMetadata(content);
      const metadataString = Object.keys(metadataContent).length > 0 ? JSON.stringify(metadataContent) : '';
      const postContent = `${serializeToHtml(content)}${metadataString}`;
      return postContent.length < 32000;
    })
    // @ts-ignore
    .test('onlyAmazonLinks', 'content_error_amazonLinks', (value, { originalValue }) => {
      if (isInternalUser) {
        // Amazon internal team members can post links at their discretion
        return true;
      }
      const text = serializeToPlaintext(originalValue, true);
      return hasOnlyAmazonLinks(text);
    })
    // @ts-ignore
    .test('onlyAmazonLinks', 'content_error_amazonLinks', (value, { originalValue }) => {
      if (isInternalUser) {
        // Amazon internal team members can post links at their discretion
        return true;
      }
      const editor = createEditor();
      editor.children = originalValue.map(defineEmptyString);
      // Selects all content
      Transforms.select(editor, {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      });
      const linkNodes = Array.from(
        Editor.nodes(editor, {
          match: (n) => !Editor.isEditor(n)
      && Element.isElement(n)
      && n.type === ElementTypes.link,
        }),
      );

      return linkNodes.every(([link]) => isValidAmazonLink((link as LinkElement).url));
    }),
  groupId: (groups: GroupsState['groups']) => yup
    .string()
    .oneOf([NONE_OPTION, ...Object.keys(groups)], 'groupId_error_invalidOption'),
  categoryId: (categories: CreateThreadState['categories']) => yup
    .string()
    .required('categoryId_error_required')
    .oneOf(Object.keys(categories), 'categoryId_error_invalidOption'),
  tags: (categories: CreateThreadState['categories']) => yup
    .array()
    .when('categoryId', (categoryId, schema) => schema.of(yup
      .string()
      .oneOf(categories[categoryId]?.tags || [], 'tags_error_invalidOption')))
    .min(1, 'tags_error_min')
    .max(5, 'tags_error_max')
    .ensure(),
  username: yup
    .string()
    .ensure()
    .required('displayName_error_required')
    .min(5, 'displayName_error_min')
    .max(20, 'displayName_error_max')
    .matches(/^\w+$/, 'displayName_error_invalidCharacters'),
  categoryIds: (categories: CreateThreadState['categories']) => yup
    .array()
    .ensure()
    .of(yup.string()
      .oneOf(Object.keys(categories), 'categoryId_error_invalidOption')),
  aboutMe: yup
    .string()
    .trim()
    .max(500, 'userProfile_aboutMe_error_max'),
  aboutMyBusiness: yup
    .string()
    .trim()
    .max(500, 'userProfile_aboutMyBusiness_error_max'),
};
